/*
 * Upgrade modal copy — single source of truth.
 *
 * RULE: Solo speaks only to individual reliability.
 *       Band speaks only to multi-user coordination.
 *       No bullet, phrase, or value prop should appear in both objects.
 *
 * Read this comment before editing. If a benefit applies to both plans,
 * it belongs in neither — surface it elsewhere (e.g. the pricing page).
 */

export interface UpgradeModalContent {
  headline: string;
  subheading: string;
  bullets: string[];
  closingLine: string;
  ctaLabel: string;
  tier: 'solo' | 'band';
}

export const SOLO_MODAL: UpgradeModalContent = {
  tier: 'solo',
  headline: 'Your setlist, ready before the downbeat.',
  subheading:
    'Solo gives you the reliability a working musician needs — your charts, your setlists, available the second you tap them.',
  bullets: [
    'Unlimited setlists and PDF charts in your library',
    'Full offline cache — every chart loads instantly, even with no signal at the venue',
    'Live Mode with large-format display, foot-pedal support, and crash recovery',
    'Personal version history so you can roll back any chart edit',
    'Fast search across your entire library by song, key, or tag',
  ],
  closingLine: "Built for the gig you're playing tonight, not the one you're hoping for.",
  ctaLabel: 'Upgrade to Solo',
};

export const BAND_MODAL: UpgradeModalContent = {
  tier: 'band',
  headline: 'One setlist. Everyone on the same version.',
  subheading:
    'Band is for groups that need to rehearse, change keys, and walk on stage knowing every member is looking at the exact same chart.',
  bullets: [
    'Shared band library — invite members and assign roles (leader, member, sub)',
    'Locked setlist versions so a last-minute edit pushes to every device at once',
    'Real-time setlist sync during the show — reorder a song and everyone sees it',
    'Per-member permissions for who can edit charts vs. only view them',
    'Sub-friendly access: share a setlist with a fill-in player without giving full library access',
  ],
  closingLine: 'Stop sending PDFs in group chats. Run the gig from one source of truth.',
  ctaLabel: 'Upgrade your Band',
};
